// components/Survey/CreateTheme.jsx
import React, { useState } from 'react';
import { themesService } from '../../services/themesService';
import { 
  PlusIcon, 
  TrashIcon, 
  XMarkIcon,
  DocumentPlusIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

const CreateTheme = ({ onThemeCreated, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    colorScheme: 'blue',
    icon: '📊',
    isPremium: false,
    estimatedTime: '10-15 min',
    categories: [{ categoryName: 'General', description: 'General questions', displayOrder: 1 }],
    questions: [
      {
        questionText: '',
        questionType: 'MultipleChoice',
        options: ['Strongly Agree', 'Agree', 'Neutral', 'Disagree', 'Strongly Disagree'],
        isRequired: true,
        displayOrder: 1,
        categoryId: 0
      }
    ]
  });
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const colorSchemes = [
    { value: 'blue', name: 'Blue', gradient: 'from-blue-500 to-cyan-500' },
    { value: 'green', name: 'Green', gradient: 'from-green-500 to-emerald-500' },
    { value: 'purple', name: 'Purple', gradient: 'from-purple-500 to-violet-500' },
    { value: 'orange', name: 'Orange', gradient: 'from-orange-500 to-amber-500' },
    { value: 'red', name: 'Red', gradient: 'from-red-500 to-rose-500' },
    { value: 'teal', name: 'Teal', gradient: 'from-teal-500 to-cyan-500' }
  ];

  const questionTypes = [
    { value: 'MultipleChoice', name: 'Multiple Choice' },
    { value: 'RatingScale', name: 'Rating Scale' },
    { value: 'Text', name: 'Text Input' },
    { value: 'YesNo', name: 'Yes/No' },
    { value: 'LikertScale', name: 'Likert Scale' }
  ];

  const icons = ['📊', '💼', '👥', '🚀', '💡', '🎯', '⭐', '❤️', '📈', '🤝'];

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const handleAddCategory = () => {
    setFormData(prev => ({
      ...prev,
      categories: [
        ...prev.categories,
        { 
          categoryName: '', 
          description: '', 
          displayOrder: prev.categories.length + 1 
        }
      ]
    }));
  };

  const handleRemoveCategory = (index) => {
    if (formData.categories.length <= 1) {
      showMessage('At least one category is required', 'error');
      return;
    }

    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateCategory = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, i) => 
        i === index ? { ...cat, [field]: value } : cat
      )
    }));
  };

  const handleAddQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          questionText: '',
          questionType: 'MultipleChoice',
          options: ['Strongly Agree', 'Agree', 'Neutral', 'Disagree', 'Strongly Disagree'],
          isRequired: true,
          displayOrder: prev.questions.length + 1,
          categoryId: 0
        }
      ]
    }));
  };

  const handleRemoveQuestion = (index) => {
    if (formData.questions.length <= 1) {
      showMessage('At least one question is required', 'error');
      return;
    }

    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateQuestion = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const handleAddOption = (questionIndex) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { ...q, options: [...q.options, 'New Option'] }
          : q
      )
    }));
  };

  const handleRemoveOption = (questionIndex, optionIndex) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              options: q.options.filter((_, oi) => oi !== optionIndex) 
            }
          : q
      )
    }));
  };

  const handleUpdateOption = (questionIndex, optionIndex, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              options: q.options.map((opt, oi) => 
                oi === optionIndex ? value : opt
              )
            }
          : q
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form
      if (!formData.name.trim()) {
        showMessage('Theme name is required', 'error');
        setLoading(false);
        return;
      }

      if (formData.categories.some(cat => !cat.categoryName.trim())) {
        showMessage('All categories must have a name', 'error');
        setLoading(false);
        return;
      }

      if (formData.questions.some(q => !q.questionText.trim())) {
        showMessage('All questions must have text', 'error');
        setLoading(false);
        return;
      }

      // Prepare data for API
      const submitData = {
        ...formData,
        categories: formData.categories.map((cat, index) => ({
          ...cat,
          displayOrder: index + 1
        })),
        questions: formData.questions.map((q, index) => ({
          ...q,
          displayOrder: index + 1,
          categoryId: q.categoryId || 0 // Will be mapped to actual category ID on backend
        }))
      };

      const result = await themesService.createTheme(submitData);
      showMessage(`Theme "${result.name}" created successfully!`, 'success');
      
      if (onThemeCreated) {
        onThemeCreated(result);
      }
    } catch (error) {
      showMessage(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <DocumentPlusIcon className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Create New Survey Theme</h2>
                <p className="text-green-100">Design a reusable survey template with categories and questions</p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="text-white hover:text-green-200 transition-colors p-2 hover:bg-green-600 rounded-lg"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {message.text && (
            <div className={`p-4 rounded-lg mb-6 ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-700' 
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Theme Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Employee Engagement Survey"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Time
                  </label>
                  <select
                    value={formData.estimatedTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimatedTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="5-10 min">5-10 minutes</option>
                    <option value="10-15 min">10-15 minutes</option>
                    <option value="15-20 min">15-20 minutes</option>
                    <option value="20-30 min">20-30 minutes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color Scheme
                  </label>
                  <select
                    value={formData.colorScheme}
                    onChange={(e) => setFormData(prev => ({ ...prev, colorScheme: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {colorSchemes.map(scheme => (
                      <option key={scheme.value} value={scheme.value}>
                        {scheme.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon
                  </label>
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {icons.map(icon => (
                      <option key={icon} value={icon}>
                        {icon} {icon}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe the purpose and use cases for this theme..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isPremium"
                    checked={formData.isPremium}
                    onChange={(e) => setFormData(prev => ({ ...prev, isPremium: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="isPremium" className="ml-2 text-sm text-gray-700">
                    Premium Theme (requires subscription)
                  </label>
                </div>
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
                <button
                  type="button"
                  onClick={handleAddCategory}
                  className="flex items-center gap-2 px-3 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Category
                </button>
              </div>

              <div className="space-y-4">
                {formData.categories.map((category, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50"
                  >
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category Name *
                        </label>
                        <input
                          type="text"
                          value={category.categoryName}
                          onChange={(e) => handleUpdateCategory(index, 'categoryName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Leadership, Culture, Benefits"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Display Order
                        </label>
                        <input
                          type="number"
                          value={category.displayOrder}
                          onChange={(e) => handleUpdateCategory(index, 'displayOrder', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          min="1"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          value={category.description}
                          onChange={(e) => handleUpdateCategory(index, 'description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Describe what this category covers..."
                        />
                      </div>
                    </div>
                    {formData.categories.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveCategory(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Questions */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Questions ({formData.questions.length})
                </h2>
                <button
                  type="button"
                  onClick={handleAddQuestion}
                  className="flex items-center gap-2 px-3 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Question
                </button>
              </div>

              <div className="space-y-6">
                {formData.questions.map((question, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-6 border border-gray-200 rounded-lg bg-gray-50"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-md font-medium text-gray-900">
                        Question {index + 1}
                      </h3>
                      {formData.questions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveQuestion(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Question Text *
                        </label>
                        <textarea
                          value={question.questionText}
                          onChange={(e) => handleUpdateQuestion(index, 'questionText', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter your question here..."
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Question Type
                        </label>
                        <select
                          value={question.questionType}
                          onChange={(e) => handleUpdateQuestion(index, 'questionType', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {questionTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category
                        </label>
                        <select
                          value={question.categoryId}
                          onChange={(e) => handleUpdateQuestion(index, 'categoryId', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {formData.categories.map((cat, catIndex) => (
                            <option key={catIndex} value={catIndex}>
                              {cat.categoryName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Display Order
                        </label>
                        <input
                          type="number"
                          value={question.displayOrder}
                          onChange={(e) => handleUpdateQuestion(index, 'displayOrder', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          min="1"
                        />
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`required-${index}`}
                          checked={question.isRequired}
                          onChange={(e) => handleUpdateQuestion(index, 'isRequired', e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor={`required-${index}`} className="ml-2 text-sm text-gray-700">
                          Required question
                        </label>
                      </div>
                    </div>

                    {/* Options for Multiple Choice Questions */}
                    {(question.questionType === 'MultipleChoice' || question.questionType === 'RatingScale') && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-medium text-gray-700">
                            Options
                          </label>
                          <button
                            type="button"
                            onClick={() => handleAddOption(index)}
                            className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                          >
                            <PlusIcon className="w-4 h-4" />
                            Add Option
                          </button>
                        </div>
                        <div className="space-y-2">
                          {question.options.map((option, optionIndex) => (
                            <div key={optionIndex} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => handleUpdateOption(index, optionIndex, e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Option text..."
                              />
                              {question.options.length > 2 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveOption(index, optionIndex)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <DocumentPlusIcon className="w-5 h-5" />
                    Create Theme
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CreateTheme;